import { useState } from 'react';
import { Box, Stack, Tabs, Tab } from '@mui/material';
import { useCurrentUser } from '@diggens/user-management';
import { POLICY_ROLES } from '@diggens/types';
import { UserInfo } from './settings/UserInfo';
import { DataPrivacy } from './settings/DataPrivacy';
import { AuditLogs } from './settings/AuditLogs';
import { TenantInfo } from './settings/TenantInfo';
import { UserManagementTab } from './settings/UserManagementTab';
import { HubSpotConnection } from './settings/HubSpotConnection';
import { SlackConnection } from './settings/SlackConnection';
import { FreydaConnection } from './settings/FreydaConnection';
import { TeamTab } from './settings/TeamTab';
import { AgentPreferences } from './settings/AgentPreferences';
import { FirmPolicies } from './settings/FirmPolicies';

type SettingsTab = 'user' | 'connections' | 'agents' | 'users' | 'team' | 'tenant' | 'privacy' | 'audit';

export function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('user');
  const { data: user } = useCurrentUser();
  const isPortco = user?.role === 'portco';
  const isAdmin = user?.role && ['super_admin', 'admin', 'billing_admin'].includes(user.role);
  // The API refuses connections to portco and external users; portco users never reach the tabs
  const canConnect = user?.role !== 'external_user';
  // A super admin manages a tenant's agents from its page under Tenants
  const isTenantAdmin = user?.role === 'admin';
  // Everyone the agents work for has policies; the firm's defaults are for the roles the API lets set them
  const hasPolicies = user?.role !== undefined && POLICY_ROLES.includes(user.role);
  const setsFirmPolicies = user?.role === 'admin' || user?.role === 'super_admin';
  // Firm-wide connections (Freyda) are for the roles the API lets connect them (ADMIN_ROLES)
  const connectsForFirm = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <Box sx={{ p: 3 }}>
      {isPortco ? (
        // Portco users only see their user info
        <UserInfo />
      ) : (
        // Everyone else sees these tabs; User Management is for admins, and Team for tenant admins
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, newValue: SettingsTab) => setActiveTab(newValue)}>
              <Tab label="User Info" value="user" />
              {canConnect && <Tab label="Connections" value="connections" />}
              {hasPolicies && <Tab label="Your agents" value="agents" />}
              {isAdmin && <Tab label="User Management" value="users" />}
              {isTenantAdmin && <Tab label="Team" value="team" />}
              <Tab label="Tenant Info" value="tenant" />
              <Tab label="Data & Privacy" value="privacy" />
              <Tab label="Audit Logs" value="audit" />
            </Tabs>
          </Box>

          {activeTab === 'user' && <UserInfo />}
          {canConnect && activeTab === 'connections' && (
            <Stack spacing={3}>
              <HubSpotConnection />
              <SlackConnection />
              {connectsForFirm && <FreydaConnection />}
            </Stack>
          )}
          {hasPolicies && activeTab === 'agents' && (
            <Stack spacing={5}>
              <AgentPreferences />
              {setsFirmPolicies && <FirmPolicies />}
            </Stack>
          )}
          {isAdmin && activeTab === 'users' && <UserManagementTab />}
          {isTenantAdmin && activeTab === 'team' && <TeamTab />}
          {activeTab === 'tenant' && <TenantInfo />}
          {activeTab === 'privacy' && <DataPrivacy />}
          {activeTab === 'audit' && <AuditLogs />}
        </>
      )}
    </Box>
  );
}
